import React, { useState } from 'react';
import {
  useGetActiveSessionsQuery,
  useRevokeSessionMutation,
  useRevokeAllOtherSessionsMutation
} from '../../../features/sessions/sessionsApiSlice';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Computer as ComputerIcon,
  Smartphone as SmartphoneIcon,
  Tablet as TabletIcon,
  DeviceUnknown as DeviceUnknownIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { HeadingWrap, Span } from '../../../Styles/CommonStyles';

const LoginSessions = () => {
  const [confirmDialog, setConfirmDialog] = useState({ open: false, sessionId: null, action: null });
  
  const {
    data: sessionsData,
    isLoading,
    isError,
    error,
    refetch
  } = useGetActiveSessionsQuery(undefined, {
    pollingInterval: 30000, // Refresh every 30 seconds
    refetchOnFocus: true
  });

  const [revokeSession, { isLoading: isRevoking }] = useRevokeSessionMutation();
  const [revokeAllOthers, { isLoading: isRevokingAll }] = useRevokeAllOtherSessionsMutation();

  const getDeviceIcon = (deviceType) => {
    if (deviceType?.includes('Mobile')) return <SmartphoneIcon />;
    if (deviceType?.includes('Tablet')) return <TabletIcon />;
    if (deviceType?.includes('Desktop')) return <ComputerIcon />;
    return <DeviceUnknownIcon />;
  };

  const getDeviceColor = (session) => {
    if (session.isCurrent) return 'primary';
    if (session.isExpiringSoon) return 'warning';
    return 'default';
  };

  const handleRevokeSession = async (tokenId) => {
    try {
      await revokeSession(tokenId).unwrap();
      setConfirmDialog({ open: false, sessionId: null, action: null });
    } catch (err) {
      console.error('Failed to revoke session:', err);
    }
  };

  const handleRevokeAllOthers = async () => {
    try {
      await revokeAllOthers().unwrap();
      setConfirmDialog({ open: false, sessionId: null, action: null });
    } catch (err) {
      console.error('Failed to revoke other sessions:', err);
    }
  };

  const openConfirmDialog = (sessionId, action) => {
    setConfirmDialog({ open: true, sessionId, action });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, sessionId: null, action: null });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load sessions: {error?.data?.message || 'Unknown error'}
        <Button onClick={refetch} startIcon={<RefreshIcon />} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  const { sessions = [], summary = {}, totalSessions = 0 } = sessionsData || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        {/* Centered Title */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <HeadingWrap>
            <Span>Login Sessions</Span>
          </HeadingWrap>
        </Box>
        
        {/* Refresh Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={refetch}
            startIcon={<RefreshIcon />}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {totalSessions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Sessions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="info.main">
                {summary.desktopDevices || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Desktop Devices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                {summary.mobileDevices || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Mobile Devices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="warning.main">
                {summary.expiringSoon || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expiring Soon
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bulk Actions */}
      {sessions.length > 1 && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<SecurityIcon />}
            onClick={() => openConfirmDialog(null, 'revokeAll')}
            disabled={isRevokingAll}
          >
            {isRevokingAll ? 'Revoking...' : 'End All Other Sessions'}
          </Button>
        </Box>
      )}

      {/* Sessions List */}
      <Grid container spacing={2}>
        {sessions.map((session) => (
          <Grid item xs={12} md={6} lg={4} key={session.tokenId}>
            <Card 
              sx={{ 
                height: '100%',
                border: session.isCurrent ? 2 : 1,
                borderColor: session.isCurrent ? 'primary.main' : 'divider',
                position: 'relative'
              }}
            >
              {session.isCurrent && (
                <Chip
                  label="Current Session"
                  color="primary"
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                />
              )}
              
              <CardContent>
                {/* Device Info */}
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  {getDeviceIcon(session.deviceType)}
                  <Box>
                    <Typography variant="h6" component="div">
                      {session.deviceInfo?.browser || 'Unknown Browser'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {session.deviceInfo?.os || 'Unknown OS'}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* Location */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {session.locationDisplay || 'Unknown Location'}
                  </Typography>
                </Stack>

                {/* Login Time */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    Logged in: {session.loginTime}
                  </Typography>
                </Stack>

                {/* Session Status */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Chip
                    label={session.isExpiringSoon ? 'Expiring Soon' : `${session.timeRemaining}h remaining`}
                    color={getDeviceColor(session)}
                    size="small"
                    icon={session.isExpiringSoon ? <WarningIcon /> : <ScheduleIcon />}
                  />
                </Stack>

                {/* Actions */}
                {!session.isCurrent && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => openConfirmDialog(session.tokenId, 'revoke')}
                    disabled={isRevoking}
                    fullWidth
                  >
                    End Session
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {sessions.length === 0 && (
        <Alert severity="info">
          No active sessions found.
        </Alert>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
        <DialogTitle>
          {confirmDialog.action === 'revokeAll' ? 'End All Other Sessions?' : 'End This Session?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'revokeAll'
              ? 'This will log you out from all other devices. You will remain logged in on this device.'
              : 'This will immediately log out this session. This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>Cancel</Button>
          <Button
            onClick={() => {
              if (confirmDialog.action === 'revokeAll') {
                handleRevokeAllOthers();
              } else {
                handleRevokeSession(confirmDialog.sessionId);
              }
            }}
            color="error"
            variant="contained"
            disabled={isRevoking || isRevokingAll}
          >
            {confirmDialog.action === 'revokeAll' ? 'End All Others' : 'End Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginSessions;